<template>
  <div
    v-if="isAuthenticated && loaded"
    id="app"
  >
    <PageNav />
    <Toast
      group="tr"
    />
    <div
      class="MainContent"
      @click="hideOverlay"
    >
      <div
        v-if="isGuest"
        class="mx-auto mt-5"
        style="width: 325px"
      >
        <h4>U heeft geen toegang</h4>
        <p>Uw gebruikersaccount heeft geen rechten toegewezen gekregen in de Workflow tool.</p>
        <p>Onze support afdeling kan u verder helpen indien dit onterecht is.</p>
        <p>
          <MailtoSupport />
        </p>
      </div>
      <div v-else>
        <router-view />
        <Tour
          v-if="showTour"
          :show-tour-introduction="showTourIntroduction"
          @end-tour="closeTour"
        />
      </div>
    </div>

    <DebugModal v-if="isDebugModalVisible" />

    <PageFooter />

    <!-- <SessionModal /> -->
  </div>

  <!-- Loading screen - loading data -->
  <div
    v-else-if="hasRolesAndAcceptedTerms"
    id="app"
    class="LoadingScreen"
  >
    <div class="MainContent">
      <div class="pt-4">
        <div
          class="m-auto"
          style="width: 200px"
        >
          <EVToolsLogo />
          <ProgressSpinner
            class="d-flex justify-content-center align-items-center"
            style="width:60px"
          />
        </div>
        <b-card
          v-if="criticalFailure"
          class="m-auto"
          style="width: 350px"
          title="EVtools Workflow"
          :sub-title="''"
        >
          <div>
            <p
              v-if="!userRole"
              class="pt-4"
            >
              <strong>Configuratie fout! </strong>Er is geen rol geselecteerd bij de gebruiker.
            </p>
            <p
              v-else-if="isCpoAndNoIdentity"
              class="pt-4"
            >
              <strong>Configuratie fout! </strong>De gebruiker heeft Cpo rol maar geen organisatie is geselecteerd.
            </p>
            <p
              v-else-if="noRoleMunicipality"
              class="pt-4"
            >
              <strong>Configuratie fout! </strong>Er is geen gemeente geselecteerd bij de gebruiker.
            </p>
            <p
              v-else
              class="pt-4"
            >
              Het laden van de applicatie configuratie is mislukt. Herlaad de pagina opnieuw.
            </p>
            <p>Neem contact op met support indien dit probleem zich blijft voordoen.</p>
          </div>

          <template #footer>
            <div style="font-size: 0.9rem">
              <i class="icon fa-fw fal fa-envelope mr-1" />
              <MailtoSupport class="card-link" />
            </div>
          </template>
        </b-card>
      </div>
    </div>
  </div>

  <!-- terms check -->
  <div
    v-else-if="rolesAreLoaded && !hasAcceptedTerms"
    id="app"
    class="LoadingScreen"
  >
    <div class="MainContent">
      <div class="pt-4">
        <div
          class="m-auto"
          style="width: 200px"
        >
          <EVToolsLogo />
        </div>
        <b-card
          class="m-auto"
          style="width: 600px"
          title="Gebruikersvoorwaarden"
        >
          <TermsText
            ref="terms"
            @setVersion="termsVersion = $event"
          />
          <div class="text-right">
            <b-spinner
              v-if="termsStatus === 'saving'"
              variant="secondary"
              small
              class="mr-3"
            />
            <b-button
              variant="primary"
              :disabled="termsStatus !== 'pending'"
              @click="handleAccept"
            >
              Ik ga akkoord
            </b-button>
          </div>
          <template #footer>
            <div style="font-size: 0.9rem">
              <i class="icon fa-fw fal fa-envelope mr-1" />
              <MailtoSupport class="card-link" />
            </div>
          </template>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import EVToolsLogo from '@/components/EVToolsLogo.vue';
import PageFooter from '@/components/PageFooter.vue';
import PageNav from '@/components/PageNav.vue';
//import Overlay from '@/components/Overlay.vue';
import TermsText, { VERSION } from '@/components/TermsText.vue';
import DebugModal from '@/components/debugger/DebugModal.vue';
import Tour from '@/components/Tour.vue';
import MailtoSupport from '@/components/common/MailtoSupport.vue';
import GeocoderCheck from '@/components/mixins/GeocoderCheck.vue';
import { tour } from '@/config';
import { uuidValidateV4 } from '@/services/validation';
import Toast from 'primevue/toast';
import ProgressSpinner  from 'primevue/progressspinner'

export default {
  name: 'App',
  components: {
    EVToolsLogo,
    PageNav,
    PageFooter,
    //Overlay,
    TermsText,
    DebugModal,
    Tour,
    MailtoSupport,
    Toast,
    ProgressSpinner
  },
  mixins: [GeocoderCheck],
  data() {
    return {
      tour: {},
      showTour: false,
      showTourIntroduction: true,
      criticalFailure: false,
      termsStatus: 'pending',
      loading: false,
      loadingStatus: {
        config: false,
        requests: false,
        workflows: false,
        pagedRequests: {
          error: 0,
          target: 0,
          current: 0
        },
        pagedWorkflows: {
          error: 0,
          target: 0,
          current: 0
        },
        municipalities: {
          error: 0,
          target: 0,
          current: 0
        }
      },
      missingMunicipalities: [],
      missingWorkflows: [],
      missingRequests: [],
      toasts: [],
      tenant: process.env.VUE_APP_TENANT,
      // Flag to indicate the connections between requests & RPs have been established
      establishedConnections: false,
      filteringStorePoints: [
        'chargingpoints/filterChargingPoints',
        'realisations/filterRealisations',
        'requests/filterRequests'
      ]
    };
  },
  computed: {
    ...mapGetters(['loaded']),
    ...mapGetters('requests', ['getRequestCounts']),
    ...mapGetters('realisations', {
      realisationprocesses: 'records',
      recordByUuid: 'recordByUuid',
      getRealisationCounts: 'getCounts'
    }),
    ...mapGetters('user', [
      'canAccessRequests',
      'canAccessRealisationProcesses',
      'canAccessPrivateProcesses',
      'canAccessAssetManagement',
      'isGuest',
      'roleDetailsMerged',
      'rolesAreLoaded',
      'securityCheck',
      'userRole'
    ]),
    ...mapGetters('debug', ['isDebugModalVisible']),
    ...mapGetters('tenant', ['getSteps', 'getProcessesConfig', 'getToasts', 'getActiveProcessTypes']),
    ...mapGetters('chargingpoints', ['chargingpoints', 'getSelectedFilterValues']),

    isAuthenticated() {
      return this.$auth.isAuthenticated && this.$auth.user;
    },
    defaultConfig() {
      //const defaultTenant = ['evtools', 'park-n-charge', 'dutch-charge', 'vlaanderen', 'amsterdam'].includes(process.env.VUE_APP_TENANT);
      return 'default' //defaultTenant ? 'default' : process.env.VUE_APP_TENANT;
    },
    userRolesAreAvailable() {
      return this.isAuthenticated && this.$auth.user[`${process.env.VUE_APP_AUTH_NAMESPACE}roles`];
    },
    // subTitle() {
    //   return this.userRolesAreAvailable ? 'De datasets worden ingeladen' : 'Authorisatie controle';
    // },
    // usesPagedLoader() {
    //   return !['amsterdam'].includes(process.env.VUE_APP_TENANT);
    // },
    // userMunicipalityCode() {
    //   if (process.env.VUE_APP_TENANT === 'amsterdam') return false;

    //   return this.userRole === 'municipality';
    // },
    // restrictedAccess() {
    //   if (process.env.VUE_APP_TENANT === 'amsterdam') return false;

    //   return this.$auth.user['https://evtools.nl/roles'].find(role => {
    //     return role.tenant === process.env.VUE_APP_TENANT && role[this.userRole]?.[0] !== '*';
    //   });
    // },
    hasAcceptedTerms() {
      return this.termsStatus === 'accepted';
    },

    hasRolesAndAcceptedTerms() {
      return this.rolesAreLoaded //&& this.hasAcceptedTerms;
    },
    isCpo() {
      return this.userRole === 'cpo';
    },
    isMUnicipalityBasedWorkflow() {
      return ['amsterdam', 'vlaanderen', 'wijk-bij-duurstede', 'woerden-oudewater', 'arnhem', 'mechelen'].includes(process.env.VUE_APP_TENANT);
    },
    hasIdentity() {
      return this.$auth.user['https://evtools.nl/identity']?.uuid;
    },
    isCpoAndNoIdentity() {
      return this.isMUnicipalityBasedWorkflow && this.isCpo && !this.hasIdentity;
    },
    noRoleMunicipality() {
      return !this.canAccessRealisationProcesses;
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(route, oldRoute) {
        // Temp. replace old realisations route and redirect
        const routeToReplace =
          (route.name === 'Realisation' && !uuidValidateV4({ id: route.params?.uuid })) ||
          (route.name === 'ProcessList' && !route.params.type);

        if (routeToReplace) {
          this.$router.replace({ name: 'ProcessList', params: { type: this.getType() } }).catch(() => {});
        }
        this.showTour = false;
        // Is there a "routeless" tour feature (route independent) that needs to be shown first //
        const genericFeature = `allRoutes|${tour.date}`;
        const routeName = tour.allRoutes && !localStorage.getItem(genericFeature) ? 'allRoutes' : route.name;
        this.handleTour({ routeName });

        // Reset all map filters, but only when navigating to a different page/record —
        // not on every step query-string change within the same process, which caused
        // an infinite update loop (repeated filter-reset -> filter-watcher -> re-dispatch)
        const isSameRecord = oldRoute && route.name === oldRoute.name && route.params.uuid === oldRoute.params.uuid;
        if (!isSameRecord) {
          this.resetAllFiltering();
        }
      }
    },
    userRolesAreAvailable(value) {
      if (value) {
        this.init();
      }
    },

    loaded(value) {
      // Trigger Geocoder for requests without coordinates
      if (value === true) {
        // Add steps dynamically to router
        this.addStepsToRouter();

        // Add assetManagement (Locaties) to router if tenant is eligible
        if (this.canAccessRealisationProcesses) {
          this.addAssetManagementRoute();
          this.addProcessStepsToRouter();
        }
        this.$nextTick(this.showToast);
        // Check the Aanvragen data
        this.runGeocoder();
      }
    },
    async hasRolesAndAcceptedTerms(value) {
      if (value === true) {
        const token = await this.$auth.getTokenSilently();
        await this.loadConfig({ token });
        this.setPageTitle();

        await this.loadWorkflowSpecs({ token });

        this.loadingStatus.config = true;
        this.loading = false;

        this.setLoadedState({ loaded: true });

      }
    },
    getActiveProcessTypes: {
      handler (types) {
        if (types.length) {
          if (this.$route.name === 'ProcessList' && !this.$route.params.type) {
            this.$router.replace({ name: 'ProcessList', params: { type: this.getType() } }).catch(() => {});
          }
        }
      }
    }
  },
  methods: {
    ...mapActions('tenant', ['loadConfig', 'loadWorkflowSpecs']),
    ...mapMutations('overlay', ['hideOverlay']),
    ...mapActions('realisations', {
      loadRealisations: 'loadRecords'
    }),
    //...mapActions('requests', ['loadRequests', 'loadRequestsByCode']),
    ...mapMutations(['setLoadedState']),
    ...mapMutations('relations', ['addConnection']),
    ...mapMutations('user', ['setRoles']),
    ...mapMutations('chargingpoints', ['replaceChargingPointsByCode']),
    init() {
      if (this.loading) return;

      this.loading = true;

      this.checkTerms();

      this.loadRoles();
      setTimeout(() => {
        this.handleTourAccess();
      }, 1000);
    },
    closeTour() {
      this.showTour = false;
      // After end of the generic tour feature, check if there is a tour on the current route
      setTimeout(() => {
        if (!localStorage.getItem(this.$route.name)) {
          this.handleTour({ routeName: this.$route.name });
        }
      }, 100);
    },
    showToast() {
      this.toasts = this.getToasts.filter(toast => toast.enabled && !toast.deleted_at)
        this.toasts.forEach(toast => {
          const hasSeenToast = sessionStorage.getItem(`toast|${toast.uuid}`)

          if (!hasSeenToast) {
            this.$toast.add({
              // In the monorepo, the warning option is 'warning', but here it is referred to as 'warn'.
              // Same logic for types danger and error
              severity: toast.type === 'warning' ? 'warn' : toast.type === 'danger' ? 'error' : toast.type,
              summary: toast.title,
              detail: toast.message,
              group: 'tr',
              life: toast.duration
            })
            sessionStorage.setItem(`toast|${toast.uuid}`, true)
          }
        }
        );
    },
    addStepsToRouter() {
      const steps = this.getProcessesConfig.realisation?.steps;
      if (!steps?.length) return;

      this.$router.addRoute('Realisation', {
        path: '/type-:type',
        name: 'NewRealisationType',
        component: () => import('@/views/StartNewRealisationStep.vue')
      });

      steps.forEach(step => {
        this.$router.addRoute('Realisation', {
          path: '/realisatie/:uuid/stap-:step',
          name: `realisationStep${step.step || step.short}`,
          component: () => import(`@/views/${step.component}.vue`)
        });
      });
    },

    addProcessStepsToRouter() {
      const componentPath = 'processes'
      const processTypes = Object.keys(this.getProcessesConfig).filter(key => key !== 'realisation');

      processTypes.forEach(type => {
        const steps = this.getProcessesConfig[type].steps;
        const hasCustomSteps = this.getProcessesConfig[type].isCustom
        const tenantFolder = hasCustomSteps ? `/${this.tenant}` : ''
        const formGeneratedType = ['private', 'brussels', 'wallonia', 'takeover'].includes(type)
        const path = formGeneratedType ? 'ProcessFlow.vue' : 'Step1.vue'

        if (['private'].includes(type)) {
          // workaround for now
          this.$router.addRoute('Process', {
            path: '/process/new-private',
            name: 'new-private',
            component: () => import(`@/views/${componentPath}/${this.defaultConfig}/${path}`)
          });
          this.$router.addRoute('Process', {
            path: '/:type/:uuid',
            name: `${type}Steps`,
            component: () => import(`@/views/${componentPath}/${this.defaultConfig}/${path}`)
          });

        } else if (['brussels', 'wallonia', 'takeover'].includes(type)) {
          this.$router.addRoute('Record', {
            path: `/process/new-${type}`,
            name: `new-${type}`,
            component: () => import('@/views/StartCustomProcess.vue')
          });

          this.$router.addRoute('Record', {
            path: '/:type/:uuid',
            name: `${type}Steps`,
            component: () => import(`@/views/${componentPath}/${this.defaultConfig}/${path}`)
          })

        } else {
          this.$router.addRoute('Process', {
            path: '/locationId-:locationId-procestype-:type',
            name: `new-${type}`,
            component: () => import(`@/views/${componentPath}/${this.defaultConfig}/${type}${tenantFolder}/${path}`)
          });

          steps.forEach((step, index) => {
            this.$router.addRoute('Process', {
              path: '/:type/:uuid/stap-:step',
              name: `${type}Step${index + 1}`,
              component: () => import(`@/views/${componentPath}/${this.defaultConfig}/${type}${tenantFolder}/Step${index + 1}.vue`)
            })
          });
        }
      });
    },

    addAssetManagementRoute() {
      this.$router.addRoute('LocationList', {
        path: '/locaties/:locationId?',
        name: 'LocationList',
        component: () => import('@/views/LocationList.vue')
      });
    },

    loadRoles() {
      let roles = this.$auth.user[`${process.env.VUE_APP_AUTH_NAMESPACE}roles`] || {};

      // Older users have only one set of roles, new users have roles per tenant
      if (Array.isArray(roles)) {
        roles = roles.find(role => role.tenant === process.env.VUE_APP_TENANT);
      }

      if (!roles) {
        roles = {
          tenant: process.env.VUE_APP_TENANT,
          admin: [],
          cpo: [],
          municipality: []
        };
      }

      this.setRoles({
        roles
      });
    },

    handleTour({ routeName }) {
      const tourStorage = Object.keys({ ...localStorage });
      this.showTourIntroduction = false; //!tourStorage.some(key => key.includes(tour.date));
      // console.log(this.showTourIntroduction, tourStorage.some(key => key.includes(tour.date)), routeName, tour[routeName])
      // When History api back/forth is used, tour overlay keeps hanging so we need to remove the class manually //
      document.body.classList.remove('v-tour--active');
      // Check if some Realisation.Step route is already seen

      // This below was never going to work like this. This was originally made for process Step routes.
      // These desctructured assigments below, are all objects and the method "startsWith" is a String prototype
      // and besides that, it can never work with operators (||). This was always going to return false.
      //const { LocationList, ProcessList, RequestList } = tour
      //const isStepTourSeen = tourStorage.some(key => key.startsWith(LocationList || ProcessList || RequestList));
      const isStepTourSeen = tourStorage.some(key => key.startsWith(routeName));
      //this.showTour = false;

      // Don't initiate the tour if realisation type needs to be selected first //
      // Here the same thing... atm. step routes are not needed anyway. Always false.
      //if (routeName?.startsWith(LocationList || ProcessList || RequestList) && !this.recordByUuid({ uuid: this.$route.params.uuid })) return;

      if (tour?.[routeName]) {
        this.$nextTick(() => {
          //const steps = tour[routeName].steps;
          // Here the same thing... atm. step routes are not needed anyway. Always false.
          // const isTourStepSeen = routeName?.startsWith(LocationList || ProcessList || RequestList) && isStepTourSeen;

          if (isStepTourSeen) {
            // If one of the Realisation.Step routes is already seen, then remove the whole tour //
            // or only the last step in the tour if more are present, to prevent repetition on other RealisationStep tours //
            // if other unseen steps need to be seen
            // if (steps?.length === 1 && steps[0].isOnAllSteps) {
            //   delete tour[routeName];
            // } else {
            //   steps.map((step, index) => {
            //     if (step.isOnAllSteps) {
            //       delete tour[routeName].steps[index];
            //     }
            //   });
            // }
          }
          //console.log('isStepTourSeen', isStepTourSeen)
          this.showTour = !!tour[routeName] && !isStepTourSeen;
        });
      }
    },

    resetAllFiltering() {
      const filters = {
        activeMunicipality: '',
        chargingpoints: [],
        realisations: [],
        requests: []
      };
      //this.filteringStorePoints.forEach(store => this.$store.dispatch(store, { filters }));
      this.$nextTick(() => {
        this.$store.dispatch('chargingpoints/loadSelectedFilterValues', { filterValues: filters });
      });
    },

    handleTourAccess() {
      Object.keys(tour).map(route => {
        const feature = tour[route].access;
        if (feature && !this.securityCheck({ attribute: feature })) {
          // Remove whole tour if user has no access to all features it presents //
          return delete tour[route];
        }

        tour[route].steps?.map((step, stepIndex) => {
          if (step.access && !this.securityCheck({ attribute: step.access })) {
            // Remove individual step if user has no access to a feature it presents //
            delete tour[route].steps[stepIndex];
          }
        });
      });
    },

    checkTerms() {
      const terms = this.$auth.user[`${process.env.VUE_APP_AUTH_NAMESPACE}terms`] || {};
      const version = VERSION.replaceAll('_', '.');

      if (Object.keys(terms).includes(version)) {
        this.termsStatus = 'accepted';
      }
    },

    async handleAccept() {
      try {
        this.termsStatus = 'saving';
        const token = await this.$auth.getTokenSilently();
        const response = await fetch('/api/useracceptterms', {
          method: 'POST',
          headers: {
            authorization: 'Bearer ' + token
          },
          body: JSON.stringify({ version: VERSION })
        });
        if (response.ok) {
          this.termsStatus = 'accepted';
        } else {
          throw new Error('Something went wrong with accepting terms');
        }
      } catch (e) {
        console.error(e);
      }
    },
    /**
     * Set the page title (seen in browser tabs)
     */
    setPageTitle() {
      let title = null;

      if (title) {
        document.title = title;
      }
    },
    getType() {
      const hasRealisationProcesses = this.getActiveProcessTypes.some(t => t.value === 'realisation')
      const route = hasRealisationProcesses ? 'realisatie' : this.getActiveProcessTypes.at(0)?.routeType
      return process.env.VUE_APP_TENANT === 'rvb' ? 'privaat' : route;
    }
  }
};
</script>

<style lang="scss">
html,
body {
  // height: 100%;
  margin: 0;
  padding: 0;
}

html {
  overflow-y: scroll;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  color: #2c3e50;
  padding: 0;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100%;
  height: 100%;
  max-height: 100%;
}

.MainContent {
  // overflow-y: scroll;
  min-height: 100vh;
  padding-top: $menu-height; // $menu-height offset for the header
  padding-bottom: 25px; // 25px offset for footer
}

.LoadingScreen {
  background: $brand-color;

  .card {
    box-shadow: 0 0 40px 4px #111118;
  }

  &__Status .done {
    color: #00b46b;
  }
}

.v-tour--active {
  .DetailGrid__sidebar {
    position: relative;
    z-index: 10;
  }

  .v-step__header__close {
    font-size: 16px;
    margin: 0;
    right: -0.8em;
    top: -0.1em;
    position: relative;
  }
}

.v-tour__target--highlighted {
  padding: 1em;

  legend {
    > span {
      display: inline-block;
      position: relative;
      top: 1em;
    }
  }
}

.Reporting + div {
  .v-tour {
    .v-step {
      max-width: 500px;
      min-width: 400px;

      .v-step__header {
        width: auto;
      }
    }
  }
}
</style>
